import React, { useState } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { Link } from "@quintype/components";
import { shape, string, object, integer, arrayOf } from "prop-types";
import { getStoryHeadline } from "./utils/story";
import Headline from "./atoms/Headline";
import { Label } from "./atoms/Label";
import { HeroImage } from "./atoms/HeroImage";
import { PublishAndReadTime } from "./atoms/PublishAndReadTime";
import { useDeviceType } from "./custom-hooks/useDeviceType";

import "./story-grid.m.css";

function StoryGridStoryItem(props) {
  const showImagePlaceholder = useSelector((state) => get(state, ["qt", "config", "showPlaceholder"]));
  const getPlaceholderStyleName = showImagePlaceholder ? "placeholder" : "";
  const headline = getStoryHeadline(props?.story);

  return (
    <Link
      href={`/${props.story.slug}`}
      styleName={`story-grid-item ${props?.firstStoryOfRow ? "left-padding-hidden" : ""} ${
        props?.lastStoryOfRow ? "right-padding-hidden" : ""
      }`}
      aria-label="story-grid-item"
    >
      <figure styleName={`story-grid-item-image ${getPlaceholderStyleName}`}>
        <HeroImage
          story={props?.story}
          widths={[135]}
          aspectRatio={[[4, 3]]}
          headline={headline}
          iconSizes={{ width: 32, height: 32 }}
          showFallbackImage={true}
        />
      </figure>
      <Label story={props?.story} />
      {headline && <Headline styleName="headline" headline={headline} />}
      <PublishAndReadTime showReadTime={true} showTimestamp={true} story={props?.story} />
    </Link>
  );
}

const storyPropType = shape({
  id: string,
  slug: string,
  "hero-image-s3-key": string,
  "hero-image-metadata": object,
  headline: string,
  "author-name": string,
});

StoryGridStoryItem.propTypes = {
  story: storyPropType,
  position: integer,
};

export function StoryGrid({ stories = [], customSectionTemplate = false }) {
  const [visibleItems, setVisibleItems] = useState(16);
  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";

  const getStoriesInRows = (stories, noOfStoriesInRow) => {
    let updatedStories = [];

    for (let i = 0; i < Math.min(stories.length, visibleItems); i += noOfStoriesInRow) {
      updatedStories.push(stories.slice(i, i + noOfStoriesInRow));
    }
    return updatedStories;
  };

  if (stories.length === 0) {
    return null;
  }

  const loadMoreStories = () => {
    setVisibleItems((prevVisibleItems) => prevVisibleItems + 8);
  };

  const noOfStoriesInRow = isMobile ? 2 : 4;
  const visibleStories = getStoriesInRows(stories, noOfStoriesInRow);
  return customSectionTemplate ? (
    <>
      <div styleName="story-grid">
        {visibleStories.map((row, index) => {
          return (
            <>
              {row.map((story, storyIndex) => {
                const firstStoryOfRow = storyIndex + 1 === 1;
                const lastStoryOfRow = storyIndex + 1 === row.length;
                return (
                  <>
                    <StoryGridStoryItem
                      story={story}
                      key={`${storyIndex}-${story.id}`}
                      position={storyIndex}
                      firstStoryOfRow={firstStoryOfRow}
                      lastStoryOfRow={lastStoryOfRow}
                      noOfStoriesInRow={noOfStoriesInRow}
                    />
                    {(storyIndex + 1) % noOfStoriesInRow !== 0 && <div styleName="grid-separartor"></div>}
                  </>
                );
              })}
              {index + 1 !== visibleStories.length && <div styleName="horizontal-separator"></div>}
            </>
          );
        })}
      </div>
      {visibleItems < stories.length ? (
        <p styleName="loadmore-button" onClick={loadMoreStories}>
          LOAD MORE
        </p>
      ) : (
        <div></div>
      )}
    </>
  ) : (
    <div className="story-grid">
      {stories.map((story, index) => (
        <StoryGridStoryItem story={story} key={`${index}-${story.id}`} position={index} />
      ))}
    </div>
  );
}

StoryGrid.propTypes = {
  stories: arrayOf(storyPropType),
};
